import { Skeleton } from '@/components/ui';
import { cn } from '@/lib/utils';

export interface PortfolioValueHeaderProps {
  /** Total portfolio value in dollars (e.g. `200`). */
  value: number;
  /** Absolute dollar change since entry. Negative renders red, positive green. Omitted = no delta line (empty portfolio). */
  delta?: number;
  /** Percentage change since entry (matches the sign of `delta`). */
  deltaPercentage?: number;
  /** Gray headline instead of the brand gradient — the zero/empty state. */
  muted?: boolean;
  /** Caption above the amount. Defaults to the portfolio's "Portfolio Value". */
  label?: string;
  /** Text trailing the delta figures. Defaults to the portfolio's "From Entry". */
  deltaSuffix?: string;
  /** Fixed fraction digits on the headline amount (2 for money balances). Omitted = locale default, as the portfolio renders. */
  fractionDigits?: number;
  className?: string;
}

/**
 * Headline summary block at the top of the Portfolio page — and the shared
 * "money hero" of the app: the Wallet page reuses it (with its own label,
 * delta suffix, and cent precision) so both pages speak one design language.
 *
 * Shows a caption, the gradient dollar amount, and a delta line with a
 * trend arrow icon.
 *
 * @example
 * ```tsx
 * <PortfolioValueHeader value={200} delta={-0.37} deltaPercentage={-6.79} />
 * ```
 */
/**
 * Loading placeholder for {@link PortfolioValueHeader} — caption, headline
 * amount, and delta line at the same sizes/alignment, so the hero doesn't
 * flash a "$0 (+0.00%)" placeholder value while the summary query loads.
 */
export function PortfolioValueHeaderSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn('flex flex-col items-center gap-1 py-3 lg:items-start lg:py-0', className)}>
      <Skeleton className="h-6 w-28 rounded" />
      <div className="flex flex-col items-center gap-1 lg:flex-row lg:items-end lg:gap-3">
        <Skeleton className="h-[60px] w-44 rounded-lg" />
        <Skeleton className="mt-1 h-4 w-40 rounded lg:mb-2 lg:mt-0" />
      </div>
    </div>
  );
}

export function PortfolioValueHeader({
  value,
  delta,
  deltaPercentage,
  muted = false,
  label = 'Portfolio Value',
  deltaSuffix = 'From Entry',
  fractionDigits,
  className,
}: PortfolioValueHeaderProps) {
  const isNegative = (delta ?? 0) < 0;
  const deltaColor = isNegative ? 'text-danger-alt' : 'text-success';

  const absDelta = Math.abs(delta ?? 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const absPct = Math.abs(deltaPercentage ?? 0).toFixed(2);

  return (
    // Mobile: centered stack (label / number / delta). Desktop (Figma 6499-163095):
    // left-aligned, delta inline beside the number, "From Entry" trailing.
    <div className={cn('flex flex-col items-center gap-1 py-3 lg:items-start lg:py-0', className)}>
      <span className="text-base font-normal text-muted">{label}</span>
      <div className="flex flex-col items-center gap-1 lg:flex-row lg:items-end lg:gap-3">
        <span
          className={cn(
            'text-[60px] font-medium leading-none tracking-[-0.02em]',
            muted
              ? 'text-primary/30'
              : 'bg-gradient-to-b from-brand-grad-3 via-brand-grad-2 to-brand-grad-1 bg-clip-text text-transparent',
          )}
        >
          $
          {fractionDigits == null
            ? value.toLocaleString()
            : value.toLocaleString('en-US', {
                minimumFractionDigits: fractionDigits,
                maximumFractionDigits: fractionDigits,
              })}
        </span>
        {delta != null && (
          <div className="mt-1 flex items-center gap-1.5 lg:mb-2 lg:mt-0">
            <span className={deltaColor}>{isNegative ? <TriangleDown /> : <TriangleUp />}</span>
            <span className={cn('text-xs font-semibold', deltaColor)}>
              ${absDelta} ({absPct}%)
            </span>
            <span className="text-xs font-light text-primary">{deltaSuffix}</span>
          </div>
        )}
      </div>
    </div>
  );
}

/** Solid up-pointing delta triangle (Figma shows filled triangles, not trend-line icons). */
function TriangleUp() {
  return (
    <svg viewBox="0 0 10 8" className="size-2.5 fill-current" aria-hidden>
      <path d="M5 0 10 8H0Z" />
    </svg>
  );
}

/** Solid down-pointing delta triangle. */
function TriangleDown() {
  return (
    <svg viewBox="0 0 10 8" className="size-2.5 fill-current" aria-hidden>
      <path d="M5 8 0 0h10Z" />
    </svg>
  );
}
